import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@insite/content-library/Components/QuoteSummaryCard";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getBillToCustomerNumber, trackAsImpersonation, trackGaMyAccountPagesNavEvent } from "@stoneage/App/Utilities/DataLayer";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    title = "title",
    showQuoteNumber = "showQuoteNumber",
    showQuoteRefNumber = "showQuoteRefNumber",
    showQuoteStatus = "showQuoteStatus",
    showQuoteRequestedDate = "showQuoteRequestedDate",
    showQuoteExpirationDate = "showQuoteExpirationDate",
    showQuoteShipToAddress = "showQuoteShipToAddress",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.title]: string;
        [fields.showQuoteNumber]: boolean;
        [fields.showQuoteRefNumber]: boolean;
        [fields.showQuoteStatus]: boolean;
        [fields.showQuoteRequestedDate]: boolean;
        [fields.showQuoteExpirationDate]: boolean;
        [fields.showQuoteShipToAddress]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        customerNumber: getBillToCustomerNumber(state),
        impersonation: trackAsImpersonation(state),
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    quoteSummaryCard?: QuoteSummaryCardStyles;
    widgetContainer?: InjectableCss;
    titleWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    tableWrapper?: InjectableCss;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataCell?: InjectableCss;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    titleWrapper: {
        css: css`
            display: flex;
            align-items: baseline;
            margin-bottom: 12px;
        `,
    },
    tableWrapper: {
        css: css`
            width: 100%;
            overflow: auto;
        `,
    },
    contentWrapper: {
        css: css``,
    },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        recentQuotesParameter.pageSize = this.props.fields.numberOfRecords;
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, canViewQuotes } = this.props;
        if (!canViewQuotes || !quotesDataView.value || quotesDataView.value.length === 0) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        const {
            title,
            showQuoteNumber,
            showQuoteRefNumber,
            showQuoteStatus,
            showQuoteRequestedDate,
            showQuoteExpirationDate,
            showQuoteShipToAddress,
        } = this.props.fields;

        return (
            <>
                <StyledWrapper {...styles.widgetContainer}>
                    <StyledWrapper {...styles.titleWrapper}>
                        <Typography
                            as="h3"
                            variant="h3"
                            css={css`
                                margin-bottom: 0;
                                text-transform: capitalize;
                            `}
                        >
                            {title}
                        </Typography>
                        <Link
                            href={myQuotesPageUrl}
                            color="secondary"
                            className="view-all-link"
                            css={css`
                                > span {
                                    display: flex;
                                    align-items: center;
                                    margin-left: 32px;
                                    font-size: 15px;
                                    font-weight: bold;
                                }
                            `}
                            // XNG-Change: track GA4
                            onClick={() => {
                                myQuotesPageUrl && trackGaMyAccountPagesNavEvent(
                                    "myacct_myquotes",
                                    myQuotesPageUrl,
                                    translate("View All"),
                                    this.props.impersonation,
                                    this.props.customerNumber
                                );
                            }}
                        >
                            {translate("View All")}
                            <Icon
                                src="ChevronRight"
                                size={14}
                                color="secondary"
                                css={css`
                                    margin-left: 5px;
                                `}
                            ></Icon>
                        </Link>
                    </StyledWrapper>
                    <StyledWrapper {...styles.contentWrapper} className="card_container__wrapper">
                        {quotesDataView && quotesDataView.value && quotesDataView.value.length !== 0 && (
                            <StyledWrapper {...styles.tableWrapper}>
                                <DataTable {...styles.dataTable} className="mobile-view">
                                    <DataTableHead {...styles.dataTableHead} className="card-list-heading">
                                        {showQuoteRequestedDate && (
                                            <DataTableHeader
                                                title={translate("Requested")}
                                                {...styles.dataTableHeaders}
                                            >
                                                {translate("Requested")}
                                            </DataTableHeader>
                                        )}
                                        {showQuoteNumber && (
                                            <DataTableHeader
                                                title={translate("Quote Number")}
                                                {...styles.dataTableHeaders}
                                            >
                                                {translate("Quote Number")}
                                            </DataTableHeader>
                                        )}
                                        {showQuoteRefNumber && (
                                            <DataTableHeader
                                                title={translate("Online Ref #")}
                                                {...styles.dataTableHeaders}
                                            >
                                                {translate("Online Ref #")}
                                            </DataTableHeader>
                                        )}
                                        {showQuoteStatus && (
                                            <DataTableHeader title={translate("Status")} {...styles.dataTableHeaders}>
                                                {translate("Status")}
                                            </DataTableHeader>
                                        )}
                                        {showQuoteExpirationDate && (
                                            <DataTableHeader title={translate("Expires")} {...styles.dataTableHeaders}>
                                                {translate("Expires")}
                                            </DataTableHeader>
                                        )}
                                        {showQuoteShipToAddress && (
                                            <DataTableHeader
                                                title={translate("Ship To Address")}
                                                {...styles.dataTableHeaders}
                                            >
                                                {translate("Ship To Address")}
                                            </DataTableHeader>
                                        )}
                                    </DataTableHead>
                                    <DataTableBody {...styles.dataTableBody} className="card-list-body">
                                        {(quotesDataView.value || []).map(
                                            quote =>
                                                quote !== null && (
                                                    <DataTableRow
                                                        {...styles.dataTableRow}
                                                        data-test-selector="recentOrders_dataLine"
                                                        key={quote.id}
                                                    >
                                                        <QuoteSummaryCard
                                                            quote={quote}
                                                            session={session}
                                                            quoteSettings={settingsCollection.quoteSettings}
                                                            extendedStyles={styles.quoteSummaryCard}
                                                            {...({ fields: this.props.fields } as any)}
                                                        />
                                                    </DataTableRow>
                                                ),
                                        )}
                                    </DataTableBody>
                                </DataTable>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Quotes Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showQuoteNumber,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
            },
            {
                name: fields.showQuoteRefNumber,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
            },
            {
                name: fields.showQuoteStatus,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
            },
            {
                name: fields.showQuoteRequestedDate,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
            },
            {
                name: fields.showQuoteExpirationDate,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
            },
            {
                name: fields.showQuoteShipToAddress,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
            },
        ],
    },
};

export default widgetModule;
